import { useCallback, useMemo } from "react";
import { defineMessages } from "react-intl";
import omit from "lodash/omit";

import FormFieldOptionsSelect from "./FormFieldOptionsSelect";
import OtherFieldForSelect from "./OtherFieldForSelect";
import inputConnect from "./inputConnect";
import { useFormatMessage } from "../Intl/Context";
import { useFieldContext } from "../Form/Field";

const toSelectOption = option => ({
  value: option.id,
  label: option.name,
  isCorrect: option.isCorrect,
  correctionText: option.correctionText
});

const CheckboxesMultiSelectInput = props => {
  const { fieldId } = useFieldContext();
  const {
    values,
    fieldName,
    options,
    updateValue,
    onChange,
    other,
    otherValue,
    otherFieldLabel,
    displayOtherField,
    maximumSelectableOptions,
    answer
  } = props;

  const t = useFormatMessage();
  const placeholderLabel = t(intlMessages.placeholder);

  const otherField = useMemo(
    () => (displayOtherField ? { label: otherFieldLabel, value: "" } : null),
    [displayOtherField, otherFieldLabel]
  );

  const selectOptions = useMemo(() => options.map(toSelectOption), [options]);

  const selectedValues = useMemo(() => {
    const currentValues = values || [];

    return other ? [...currentValues, ""] : currentValues;
  }, [values, other]);

  const update = useCallback(
    data => {
      const currentData = { values, other, otherValue };

      updateValue({ ...currentData, ...data });
    },
    [other, otherValue, updateValue, values]
  );

  const handleOptionsChanged = useCallback(
    options => {
      const values = options.reduce((newValues, { value }) => {
        if (value && value !== "") newValues.push(value);
        return newValues;
      }, []);
      const other = options.some(option => option.value === "");
      const newOtherValue = other ? otherValue : "";

      update({ values, other, otherValue: newOtherValue });

      if (onChange) onChange(options);
    },
    [update, otherValue, onChange]
  );

  const isOptionDisabled = () => selectedValues.length == maximumSelectableOptions;

  const passthroughProps = omit(props, [
    "defaultValues",
    "values",
    "value",
    "options",
    "updateValue",
    "fieldName",
    "formKey",
    "onChange",
    "answer"
  ]);

  return (
    <>
      <FormFieldOptionsSelect
        inputId={fieldId}
        className="react-select"
        classNamePrefix="react-select"
        name={fieldName}
        answer={answer}
        options={selectOptions}
        otherField={otherField}
        selectedValues={selectedValues}
        onOptionsChanged={handleOptionsChanged}
        isMulti
        isClearable
        isOptionDisabled={isOptionDisabled}
        placeholder={placeholderLabel}
        styles={{ menu: provided => ({ ...provided, zIndex: 2000 }) }}
        {...passthroughProps}
      />

      <OtherFieldForSelect
        otherFieldLabel={otherFieldLabel}
        other={other}
        otherValue={otherValue}
        onChange={update}
      />
    </>
  );
};

export default inputConnect(CheckboxesMultiSelectInput, {
  fields: { values: [], other: false, otherValue: "" }
});

const intlMessages = defineMessages({
  placeholder: {
    id: "input.checkboxesMultiSelect.placeholder",
    defaultMessage: "Sélectionnez une ou plusieurs options ..."
  }
});
